import React, { useEffect, useState } from "react";
import { Loader2, Trash2, Save } from "lucide-react";
import { argus, aErr, Btn, Label, inputCls } from "@/components/argus/ui";
import ArgusSources from "@/components/argus/ArgusSources";
import ArgusLookups from "@/components/argus/ArgusLookups";
import { ArgusClaims, ArgusTimeline, ArgusLetters } from "@/components/argus/ArgusTools";

const TABS = [["sources", "Sources"], ["lookups", "Find records"], ["claims", "Claim checker"], ["timeline", "Timeline"], ["letters", "Records requests"], ["notes", "Notes"]];
export const STATUSES = ["lead", "investigating", "verifying", "ready", "closed"];

export default function ArgusCase({ caseId, onUpdated, onDeleted }) {
  const [c, setC] = useState(null); const [tab, setTab] = useState("sources");
  const [sources, setSources] = useState([]); const [claims, setClaims] = useState([]); const [letters, setLetters] = useState([]);
  const [notes, setNotes] = useState(""); const [saving, setSaving] = useState(false); const [err, setErr] = useState("");

  useEffect(() => {
    setC(null); setTab("sources");
    argus.get(`/cases/${caseId}`).then(({ data }) => {
      setC(data); setSources(data.sources || []); setClaims(data.claims || []); setLetters(data.letters || []); setNotes(data.notes || "");
    }).catch(e => setErr(aErr(e)));
  }, [caseId]);

  const patch = async (body) => {
    setSaving(true);
    try { const { data } = await argus.patch(`/cases/${caseId}`, body); setC(x => ({ ...x, ...data })); onUpdated(data); }
    catch (e) { setErr(aErr(e)); } finally { setSaving(false); }
  };
  const del = async () => {
    if (!window.confirm(`Delete case "${c.title}" and all its sources?`)) return;
    await argus.del(`/cases/${caseId}`); onDeleted(caseId);
  };

  if (err && !c) return <div className="text-red-300 p-6">{err}</div>;
  if (!c) return <div className="p-6 text-white/50 flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Opening case…</div>;
  return (
    <div className="p-5 sm:p-8" data-testid="argus-case">
      <div className="flex items-start gap-3 flex-wrap">
        <div className="basis-full sm:basis-auto flex-1 min-w-0 sm:min-w-[200px]">
          <Label>Case file · opened {new Date(c.created_at).toLocaleDateString()}</Label>
          <input className="mt-1 w-full bg-transparent argus-display text-3xl font-bold focus:outline-none border-b border-transparent focus:border-[#10b981]/40" defaultValue={c.title} onBlur={e => e.target.value.trim() !== c.title && e.target.value.trim().length > 1 && patch({ title: e.target.value.trim() })} data-testid="argus-case-title" />
        </div>
        <select className={`${inputCls} !w-auto capitalize`} value={c.status} onChange={e => patch({ status: e.target.value })} data-testid="argus-case-status">
          {STATUSES.map(s => <option key={s} value={s}>{s}</option>)}
        </select>
        <Btn kind="danger" onClick={del} data-testid="argus-case-delete"><Trash2 className="w-3.5 h-3.5" /> Delete</Btn>
      </div>
      <textarea className={`${inputCls} mt-3 min-h-[50px]`} defaultValue={c.summary} placeholder="One-line summary of what you're investigating" onBlur={e => e.target.value !== c.summary && patch({ summary: e.target.value })} data-testid="argus-case-summary" />

      <div className="mt-6 flex gap-1 border-b border-white/10 overflow-x-auto" data-testid="argus-case-tabs">
        {TABS.map(([id, l]) => (
          <button key={id} type="button" onClick={() => setTab(id)} className={`px-3 py-2 text-xs font-mono uppercase tracking-widest border-b-2 whitespace-nowrap transition-colors ${tab === id ? "border-[#10b981] text-white" : "border-transparent text-white/45 hover:text-white/80"}`} data-testid={`argus-tab-${id}`}>
            {l}{id === "sources" ? ` · ${sources.length}` : id === "claims" ? ` · ${claims.length}` : ""}
          </button>
        ))}
      </div>
      <div className="mt-5">
        {tab === "sources" && <ArgusSources caseId={caseId} sources={sources} setSources={setSources} />}
        {tab === "lookups" && <ArgusLookups sources={sources} />}
        {tab === "claims" && <ArgusClaims caseId={caseId} claims={claims} setClaims={setClaims} sources={sources} />}
        {tab === "timeline" && <ArgusTimeline caseId={caseId} />}
        {tab === "letters" && <ArgusLetters caseId={caseId} letters={letters} setLetters={setLetters} />}
        {tab === "notes" && (
          <div data-testid="argus-notes">
            <textarea className={`${inputCls} min-h-[360px] argus-display leading-relaxed`} value={notes} onChange={e => setNotes(e.target.value)} placeholder="Working notes, leads, people to call…" data-testid="argus-notes-input" />
            <Btn busy={saving} onClick={() => patch({ notes })} className="mt-2" data-testid="argus-notes-save"><Save className="w-3.5 h-3.5" /> Save notes</Btn>
          </div>
        )}
      </div>
    </div>
  );
}
